import { StateService } from "@uirouter/angular";
import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Observable, Subscription, take, takeUntil } from "rxjs";
import { IIndicator } from "@gtmhub/error-handling";
import { QuantivePlusSuggestions } from "@gtmhub/goals/models";
import { ITaskCreatedArgs, ITaskDeletedArgs, ITaskUpdatedArgs } from "@gtmhub/tasks/task.events";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { Goal } from "@webapp/okrs/goals/models/goal.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { PermissionsFacade } from "@webapp/permissions/services/permissions-facade.service";
import { IQuantivePlusAcceptedTask, IQuantivePlusSuggestionStatus, IQuantivePlusTask } from "@webapp/platform-intelligence/quantive-plus/models";
import { PiImprovementFacade } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/pi-improvement-facade.service";
import { generateImprovementPayload } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/utils";
import {
  PIStateProcessorInstanceSubType,
  PiStateProcessorInstance,
} from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/state-processor/pi-state-processor.models";
import { PiFeatureVisibilityFacade } from "@webapp/platform-intelligence/shared/services/pi-feature-visibility-facade.service";
import { PiSuggestionSharedGoalStateService } from "@webapp/platform-intelligence/shared/services/pi-suggestion-shared-goal-state.service";
import { PiSuggestionSharedMetricStateService } from "@webapp/platform-intelligence/shared/services/pi-suggestion-shared-metric-state.service";
import { getPiErrorData } from "@webapp/platform-intelligence/shared/utils/pi-errors.utils";
import { FlowName, KeyResultFlowName, ObjectiveFlowName } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { Task, TaskParentType } from "@webapp/tasks/models/tasks.models";
import { TasksFacade } from "@webapp/tasks/services/task-facade.service";

@Component({
  selector: "okr-tasks",
  templateUrl: "okr-tasks.component.html",
  styleUrls: ["okr-tasks.component.less"],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [DestroyService],
})
export class OkrTasksComponent implements OnInit, OnDestroy {
  @Input()
  public parentId: string;
  @Input()
  public item: Goal | Metric;
  @Input()
  public parentType: TaskParentType;
  @Input()
  public suggestions: QuantivePlusSuggestions;
  @Input()
  public suggestionsStatus: IQuantivePlusSuggestionStatus;
  @Input()
  public hasInlineSuggestions: boolean;
  @Input()
  public tasksInstance: PiStateProcessorInstance;
  @Input()
  public isRemoveBtnAvailable: boolean;

  @Output()
  public readonly handleKrTaskSuggestionsOnTaskAdded = new EventEmitter<{ suggestionIndex: number; currentMetricTasksCount: number; flowName: KeyResultFlowName }>();
  @Output()
  public readonly handleKrTaskSuggestionsOnTaskRemoved = new EventEmitter<{ currentMetricTasksCount: number; flowName: KeyResultFlowName }>();
  @Output()
  public readonly handleGoalTaskSuggestionsOnTaskAdded = new EventEmitter<{ suggestionIndex: number; currentGoalTasksCount: number; flowName: ObjectiveFlowName }>();
  @Output()
  public readonly handleGoalTaskSuggestionsOnTaskRemoved = new EventEmitter<{ currentGoalTasksCount: number; flowName: ObjectiveFlowName }>();
  @Output()
  public readonly toggleContextualSuggestion = new EventEmitter<{ state: boolean; entityType: PIStateProcessorInstanceSubType }>();

  public taskList: Task[] = [];
  public hasManageTasksPermission$: Observable<boolean>;
  public isSuggestingTasksToOKRsEnabled: boolean;
  public indicators: { loading?: IIndicator } = {};
  public piContextualSuggestionsEnabled: boolean;
  public isTasksSuggestionVisible: boolean;
  private subscriptions: Subscription[] = [];

  constructor(
    private permissionsFacade: PermissionsFacade,
    private destroyService$: DestroyService,
    private tasksFacade: TasksFacade,
    private stateService: StateService,
    private broadcastService: BroadcastService,
    private piImprovementFacade: PiImprovementFacade,
    private piFeatureVisibilityFacade: PiFeatureVisibilityFacade,
    private piSuggestionSharedMetricStateService: PiSuggestionSharedMetricStateService,
    private piSuggestionSharedGoalStateService: PiSuggestionSharedGoalStateService,
    private featureToggleFacade: FeatureTogglesFacade
  ) {
    this.subscriptions.push(
      this.broadcastService.on("taskCreated").subscribe((args: ITaskCreatedArgs) => {
        this.onTaskCreated(args);
      })
    );

    this.subscriptions.push(
      this.broadcastService.on("taskUpdated").subscribe((args: ITaskUpdatedArgs) => {
        this.onTaskUpdated(args);
      })
    );

    this.subscriptions.push(
      this.broadcastService.on("taskDeleted").subscribe((args: ITaskDeletedArgs) => {
        this.onTaskDeleted(args);
      })
    );

    this.featureToggleFacade
      .isFeatureAvailable$(FeatureFlag.PiContextualSuggestions)
      .pipe(take(1))
      .subscribe((enabled) => {
        this.piContextualSuggestionsEnabled = enabled;
        this.isTasksSuggestionVisible = true;

        // we make them hidden if feature is enabled, otherwise this is working in contrast with the pi-feedback-card
        // TODO: iliya - remove this feature check when we remove the isPiFeedbackCardEnabled checks
        if (enabled) {
          this.isTasksSuggestionVisible = false;
        }
      });
  }

  public get isAtLeastOneTaskHidden(): boolean {
    return this.suggestions?.tasks?.some((task) => task.isHidden);
  }

  public setTasksSuggestionVisibility = (): void => {
    if (!this.isAtLeastOneTaskHidden) {
      this.isTasksSuggestionVisible = !this.isTasksSuggestionVisible;
      this.toggleContextualSuggestion.emit({ state: this.isTasksSuggestionVisible, entityType: "tasks" });
      return;
    }

    this.suggestions.tasks.forEach((task) => (task.isHidden = false));
  };

  public ngOnInit(): void {
    this.hasManageTasksPermission$ = this.permissionsFacade.hasPermission$("ManageTasks");

    if (this.parentType === "goal") {
      this.piSuggestionSharedGoalStateService
        .getSingleTask$()
        .pipe(takeUntil(this.destroyService$))
        .subscribe({
          next: (args) => this.onTaskSubjectUpdated(args),
        });
    }

    if (this.parentType === "metric") {
      this.piSuggestionSharedMetricStateService
        .getSingleTask$()
        .pipe(takeUntil(this.destroyService$))
        .subscribe({
          next: (args) => this.onTaskSubjectUpdated(args),
        });
    }

    this.loadTasks();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  public trackById(index: number, field: Task): string {
    return field.id;
  }

  public onQuantivePlusTaskCreated(args: IQuantivePlusAcceptedTask): void {
    this.broadcastService.emit("taskCreated", {
      task: args.task,
      sessionId: args.sessionId,
      quantivePlusSuggestionIndex: args.quantivePlusSuggestionIndex,
      isQuantivePlusOrigin: true,
      flowName: args.flowName,
    });
  }

  public goToCreateTaskModal = (): void => {
    this.stateService.go("._createTask.createTask", { parentType: this.parentType });
  };

  private loadTasks = (): void => {
    this.piFeatureVisibilityFacade
      .isSuggestingTasksToOKRsVisible$()
      .pipe(take(1))
      .subscribe((enabled) => {
        this.isSuggestingTasksToOKRsEnabled = enabled;
      });

    this.indicators.loading = { progress: true };

    this.tasksFacade
      .getAll$({ filter: { $and: [{ parentId: this.parentId }, { $or: [{ archived: { $exists: false } }, { archived: false }] }] }, sort: ["-dueDate,dateCreated"] })
      .pipe(take(1))
      .subscribe((tasks) => {
        delete this.indicators.loading;
        this.taskList = tasks.items;

        if (this.parentType === "goal") {
          this.piSuggestionSharedGoalStateService.setTaskList(this.taskList);
        }
        if (this.parentType === "metric") {
          this.piSuggestionSharedMetricStateService.setTaskList(this.taskList);
        }
      });
  };

  private onTaskCreated = (args: ITaskCreatedArgs): void => {
    if (args.task.parentId === this.parentId) {
      if (args.isQuantivePlusOrigin) {
        this.taskList.push(args.task);
      } else {
        this.taskList.unshift(args.task);
      }

      if (this.isSuggestingTasksToOKRsEnabled) {
        this.handleSuggestionsOnTaskAdded({
          parentType: args.task.parentType,
          currentMetricTasksCount: this.taskList.length,
          currentGoalTasksCount: this.taskList.length,
          ...(args.isQuantivePlusOrigin && { suggestionIndex: args.quantivePlusSuggestionIndex }),
          flowName: args.flowName,
        });
      }
    }
  };

  private onTaskDeleted = (args: ITaskDeletedArgs): void => {
    this.taskList = this.taskList.filter((task) => task.id !== args.task.id);

    if (this.isSuggestingTasksToOKRsEnabled) {
      this.handleSuggestionsOnTaskRemoved({ parentType: args.task.parentType, currentTasksCount: this.taskList.length });
    }
  };

  private onTaskUpdated = (args: ITaskUpdatedArgs): void => {
    const taskIndex = this.taskList.findIndex((task) => task.id === args.task.id);
    const taskIsInList = taskIndex >= 0;

    // if task changed parent => add to task list
    if (!taskIsInList) {
      if (args.task.parentId === this.parentId) {
        this.taskList.unshift(args.task);

        if (this.isSuggestingTasksToOKRsEnabled) {
          this.handleSuggestionsOnTaskAdded({
            parentType: args.task.parentType,
            currentMetricTasksCount: this.taskList.length,
            currentGoalTasksCount: this.taskList.length,
          });
        }
      }

      return;
    }

    // if parent of the task is changed or is archived, remove it from the task list
    if (this.parentId !== args.task.parentId || args.task.archived) {
      this.taskList.splice(taskIndex, 1);

      if (this.isSuggestingTasksToOKRsEnabled) {
        this.handleSuggestionsOnTaskRemoved({ parentType: this.parentType, currentTasksCount: this.taskList.length });
      }

      return;
    }

    // and update task in list
    this.taskList[taskIndex] = args.task;
  };

  private handleSuggestionsOnTaskAdded = (args: {
    parentType: TaskParentType;
    suggestionIndex?: number;
    currentMetricTasksCount: number;
    currentGoalTasksCount: number;
    flowName?: FlowName;
  }): void => {
    if (args.parentType === "goal") {
      this.handleGoalTaskSuggestionsOnTaskAdded.emit(
        (args.suggestionIndex || args.suggestionIndex === 0) && {
          suggestionIndex: args.suggestionIndex,
          currentGoalTasksCount: args.currentGoalTasksCount,
          flowName: args.flowName as ObjectiveFlowName,
        }
      );
    }

    if (args.parentType === "metric") {
      this.handleKrTaskSuggestionsOnTaskAdded.emit(
        (args.suggestionIndex || args.suggestionIndex === 0) && {
          suggestionIndex: args.suggestionIndex,
          currentMetricTasksCount: args.currentMetricTasksCount,
          flowName: args.flowName as KeyResultFlowName,
        }
      );
    }
  };

  private handleSuggestionsOnTaskRemoved = (args: { parentType: TaskParentType; currentTasksCount: number; flowName?: FlowName }): void => {
    if (args.parentType === "goal") {
      this.handleGoalTaskSuggestionsOnTaskRemoved.emit({ currentGoalTasksCount: args.currentTasksCount, flowName: args.flowName as ObjectiveFlowName });
    }

    if (args.parentType === "metric") {
      this.handleKrTaskSuggestionsOnTaskRemoved.emit({ currentMetricTasksCount: args.currentTasksCount, flowName: args.flowName as KeyResultFlowName });
    }
  };

  private onTaskSubjectUpdated(args: Partial<IQuantivePlusAcceptedTask>): void {
    if (args.type === "replacing") {
      const clonedSuggestions = angular.copy(this.suggestions.tasks);
      const suggestion = clonedSuggestions[args.quantivePlusSuggestionIndex];
      suggestion.title = args.newSuggestionName;

      this.suggestions.tasks = clonedSuggestions;

      return;
    }

    if (args.type === "improving") {
      const suggestion = this.suggestions.tasks[args.quantivePlusSuggestionIndex];
      const clonedSuggestion = angular.copy(this.suggestions.tasks[args.quantivePlusSuggestionIndex]);
      delete clonedSuggestion.isLoading;

      const payload = generateImprovementPayload({
        subEntityType: "metricsAndTasks",
        instruction: args.improvement,
        suggestion: clonedSuggestion,
        isAskAI: true,
        improveSuggestionContext: args.improveSuggestionContext,
      });

      this.postTaskImprovement(payload, suggestion, args.quantivePlusSuggestionIndex);
      return;
    }

    if (args.type === "backToSuggestion") {
      const suggestion = this.suggestions.tasks[args.quantivePlusSuggestionIndex];

      const clonedSuggestion = (({ description, title, id }: IQuantivePlusTask): IQuantivePlusTask => ({
        description,
        title,
        id,
      }))(suggestion);

      this.suggestions.tasks[args.quantivePlusSuggestionIndex] = clonedSuggestion;

      return;
    }

    if (args.task) {
      this.onQuantivePlusTaskCreated(args as IQuantivePlusAcceptedTask);
    }
  }

  private postTaskImprovement(payload, suggestion: IQuantivePlusTask, suggestionIndex: number): void {
    suggestion.isLoading = true;

    this.piImprovementFacade
      .postImprovement<IQuantivePlusTask>(payload)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          suggestion.isLoading = false;

          const clonedSuggestions = angular.copy(this.suggestions.tasks);
          clonedSuggestions[suggestionIndex] = data.suggestions.improvement;

          this.suggestions.tasks = clonedSuggestions;
        },
        error: (err: HttpErrorResponse) => {
          suggestion.isError = true;
          suggestion.isLoading = false;
          suggestion.errorData = getPiErrorData("task", err);
        },
      });
  }
}
